// BL598 Phase 2 — `gridctl get entities [--grid <slug>]`.
//
// kubectl-noun replacement for the deleted `cloudgrid list`. Flat listing of
// every entity in a grid — one row per entity, columns NAME, KIND, STATUS,
// URL. Distinct from `gridctl grid`, which renders the status-grouped hub view.
//
// `--grid <slug>` lists another grid you belong to (the grid IS the org
// namespace); without it, the active grid is used. The runner keeps the
// `(client, opts)` shape so it stays unit-testable against MockV2Client.
//
// Flags:
//   --grid <slug>                    list a specific grid (defaults to active)
//   --kind <app|agent|inspiration>   filter by entity kind
//   --status <charged|live|dark|archived>   filter by status
//   --space <slug>                   only entities scoped to a space
//   --archived                       include archived entities
//   --json                           machine-readable output

import { Command } from 'commander';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { CloudGridError } from '../../utils.js';
import type {
  V2RuntimeSummary,
  V2InspirationSummary,
} from '../../../../shared/src/v1-types.js';

type Kind = 'app' | 'agent' | 'inspiration';
type Realm = 'runtime' | 'inspiration';
type ListedStatus = 'live' | 'charged' | 'dark' | 'archived';

export interface GetEntitiesOptions {
  grid?: string;
  kind?: string;
  status?: string;
  space?: string;
  archived?: boolean;
  json?: boolean;
  output?: string;
}

interface Row {
  entity_id: string;
  name: string;
  kind: Kind;
  realm: Realm;
  status: ListedStatus;
  url: string;
  updated_at: string | null;
}

const VALID_KINDS = new Set<Kind>(['app', 'agent', 'inspiration']);
const VALID_STATUSES = new Set<ListedStatus>(['charged', 'live', 'dark', 'archived']);

function mapRuntimeStatus(s: V2RuntimeSummary['status']): ListedStatus {
  switch (s) {
    case 'live':
    case 'live_unhealthy':
      return 'live';
    case 'charged':
      return 'charged';
    case 'stopped':
      return 'dark';
    // Backlog 015-A / 070-A — never render an in-flight ('building') or failed
    // ('build_failed' / 'rollout_failed') deploy as 'live'. Map to 'dark' (not
    // serving) until the list buckets gain dedicated states.
    case 'building':
    case 'build_failed':
    case 'rollout_failed':
      return 'dark';
    case 'archived':
    case 'expired':
      return 'archived';
  }
}

async function resolveSpaceId(client: IV2Client, slug: string): Promise<string> {
  const { spaces } = await client.get<{ spaces: Array<{ space_id: string; slug: string }> }>(
    '/api/v2/spaces',
  );
  const match = spaces.find((s) => s.slug === slug);
  if (!match) {
    throw new CloudGridError(`Space '${slug}' not found in this grid.`);
  }
  return match.space_id;
}

async function fetchRuntimes(
  client: IV2Client,
  opts: { kind?: 'app' | 'agent'; spaceId?: string; includeArchived: boolean },
): Promise<Row[]> {
  const qs: Record<string, string | number | undefined> = {
    kind: opts.kind ?? 'all',
    limit: 100,
  };
  if (opts.spaceId) qs['space_id'] = opts.spaceId;
  if (opts.includeArchived) qs['include_archived'] = 'true';
  const resp = await client.get<{ items: V2RuntimeSummary[]; next_cursor: string | null }>(
    '/api/v2/runtimes',
    qs,
  );
  return (resp.items ?? []).map((r) => ({
    entity_id: r.entity_id,
    name: r.name,
    kind: r.kind as Kind,
    realm: 'runtime' as const,
    status: mapRuntimeStatus(r.status),
    url: r.url,
    updated_at: r.updated_at ?? null,
  }));
}

async function fetchInspirations(
  client: IV2Client,
  opts: { spaceSlug?: string; includeArchived: boolean },
): Promise<Row[]> {
  const qs: Record<string, string | number | undefined> = { limit: 100 };
  if (opts.spaceSlug) qs['space'] = opts.spaceSlug;
  if (opts.includeArchived) qs['include_archived'] = 'true';
  const resp = await client.get<{ items: V2InspirationSummary[]; next_cursor: string | null }>(
    '/api/v2/inspirations',
    qs,
  );
  return (resp.items ?? []).map((i) => ({
    entity_id: i.entity_id,
    name: i.name,
    kind: 'inspiration' as const,
    realm: 'inspiration' as const,
    status: 'live' as const,
    url: i.url,
    updated_at: null,
  }));
}

function renderTable(rows: Row[], wide: boolean): string {
  if (rows.length === 0) return 'No entities in this grid.';
  // `-o wide` adds the last-updated time + entity id columns (kubectl parity).
  const header = wide
    ? ['NAME', 'KIND', 'STATUS', 'URL', 'UPDATED', 'ENTITY_ID']
    : ['NAME', 'KIND', 'STATUS', 'URL'];
  const data = rows.map((r) => {
    const base = [
      r.name,
      r.kind,
      r.status,
      r.url.replace(/^https?:\/\//, ''),
    ];
    return wide ? [...base, r.updated_at ?? '—', r.entity_id] : base;
  });
  const widths = header.map((h, i) =>
    Math.max(h.length, ...data.map((row) => row[i].length)),
  );
  const fmt = (row: string[]) =>
    row.map((cell, i) => cell.padEnd(widths[i])).join('  ').trimEnd();
  const lines = [fmt(header), fmt(widths.map((w) => '-'.repeat(w)))];
  for (const row of data) lines.push(fmt(row));
  return lines.join('\n');
}

export async function runGetEntities(
  client: IV2Client,
  opts: GetEntitiesOptions,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });

  if (opts.kind && !VALID_KINDS.has(opts.kind as Kind)) {
    throw new CloudGridError(
      `Unknown --kind '${opts.kind}'. Allowed values: app, agent, inspiration.`,
    );
  }
  if (opts.status && !VALID_STATUSES.has(opts.status as ListedStatus)) {
    throw new CloudGridError(
      `Unknown --status '${opts.status}'. Allowed values: charged, live, dark, archived.`,
    );
  }

  const includeArchived = !!opts.archived || opts.status === 'archived';
  const kind = opts.kind as Kind | undefined;

  const wantRuntimes = !kind || kind === 'app' || kind === 'agent';
  const wantInspirations = !kind || kind === 'inspiration';
  const runtimeKind = kind === 'app' || kind === 'agent' ? kind : undefined;

  let spaceId: string | undefined;
  if (opts.space && wantRuntimes) {
    spaceId = await resolveSpaceId(client, opts.space);
  }

  const fetches: Array<Promise<Row[]>> = [];
  if (wantRuntimes) {
    fetches.push(fetchRuntimes(client, { kind: runtimeKind, spaceId, includeArchived }));
  }
  if (wantInspirations) {
    fetches.push(fetchInspirations(client, { spaceSlug: opts.space, includeArchived }));
  }
  const grouped = await Promise.all(fetches);
  let rows = grouped.flat();

  if (opts.status) {
    rows = rows.filter((r) => r.status === (opts.status as ListedStatus));
  }

  rows.sort((a, b) => a.name.localeCompare(b.name));

  out.json(() => ({
    total: rows.length,
    items: rows.map((r) => ({
      entity_id: r.entity_id,
      name: r.name,
      kind: r.kind,
      realm: r.realm,
      status: r.status,
      url: r.url,
      updated_at: r.updated_at,
    })),
  }));

  out.human(() => renderTable(rows, out.isWide));
}

export const getEntitiesCommand = addOutputOption(
  new Command('entities')
    .description('List entities in a grid')
    .option('--grid <slug>', 'Grid to list (defaults to the active grid)')
    .option('--kind <kind>', 'filter by kind (app|agent|inspiration)')
    .option('--status <status>', 'filter by status (charged|live|dark|archived)')
    .option('--space <slug>', 'only entities scoped to a space')
    .option('--archived', 'include archived entities')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (opts: GetEntitiesOptions) => {
  try {
    const client = await getV2Client(opts.grid ?? undefined);
    await runGetEntities(client, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
